import { apiBaseUrl } from './env';

/**
 * Access-token custody for the admin app.
 *
 * The token is held in a module variable and never written to `localStorage` or
 * a readable cookie: anything a script can read, an XSS payload can exfiltrate.
 * Durability comes from the HttpOnly refresh cookie the API sets at login — on
 * a page reload the app calls `refreshSession()` and gets a fresh access token
 * without the user signing in again.
 */

export interface AdminProfile {
  id: string;
  email: string;
  mobile?: string | null;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  permissions: string[];
}

let accessToken: string | null = null;

/** In-flight refresh, so ten parallel 401s trigger one refresh, not ten. */
let refreshInFlight: Promise<string | null> | null = null;

const subscribers = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  subscribers.forEach((notify) => notify(token));
}

/** Subscribe to sign-in/sign-out so the UI can react to a session ending. */
export function onSessionChange(listener: (token: string | null) => void): () => void {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
}

interface Envelope<T> {
  success: boolean;
  data: T;
  message: string;
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * Returns `null` rather than throwing when there is no valid session — "not
 * signed in" is the normal state on a cold load, not an error.
 */
export async function refreshSession(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        setAccessToken(null);
        return null;
      }

      const envelope = (await response.json()) as Envelope<{ accessToken: string }>;

      setAccessToken(envelope.data.accessToken);
      return envelope.data.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
